import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack } from "lucide-react";
import { toast } from "sonner";
import { TAPIN_RADIO_LOGO } from "../../lib/radioBrand";

/**
 * SeratoDeck — realistic turntable + console-style channel strip.
 *
 * Top bar:      Track · Artist · BPM · Time
 * Hot cues:     8 colored pads (click = set/jump, shift+click = clear)
 * Turntable:    Realistic platter with Tap IN Radio slipmat, spinning vinyl
 *               grooves, tonearm, and stroboscopic pitch markers.
 * Channel:      GAIN + HI / MID / LOW EQ knobs, TEMPO fader, VOL fader.
 * Transport:    CUE / SYNC / PLAY pill buttons.
 *
 * EQ + GAIN + TEMPO are visual-only for this pass (no real DSP yet) — but
 * the state is wired through parent `audioRef` for future integration with
 * BiquadFilterNode and playbackRate.
 */
export default function SeratoDeck({
  side,
  track,
  audioRef,
  playing,
  onTogglePlay,
  onCue,
  onClear,
  accent = "sky",
  bpm = null,
  bpmAnalyzing = false,
  peerTempo = 0.5,
  peerBpm = null,
  peerPlaying = false,
  peerLoaded = false,    // true when the peer deck has a track loaded — used to decide between "no peer track" and "BPM not detected" toast variants
  storageKey = "global",
  onChannelChange,
  onScratchingChange,    // (side, bool) — bubbled up so the engine can force-open the channel gain
  cueEnabled = false,
  onToggleCue,
}) {
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [hotCues, setHotCues] = useState(Array(8).fill(null));
  const [gain, setGain] = useState(0.5);
  const [eqHi, setEqHi] = useState(0.5);
  const [eqMid, setEqMid] = useState(0.5);
  const [eqLow, setEqLow] = useState(0.5);
  const [tempo, setTempo] = useState(0.5);
  // Ref mirror so closures captured at scratch-pointerdown time can read
  // the most recent tempo at scratch-pointerup time (otherwise SYNC during a
  // scratch would be overwritten by the stale captured value).
  const tempoRef = useRef(0.5);
  useEffect(() => { tempoRef.current = tempo; }, [tempo]);
  const [chanVol, setChanVol] = useState(0.85);
  // LOOP state — persisted live only (cleared on track change)
  const [loopIn, setLoopIn] = useState(null);
  const [loopOut, setLoopOut] = useState(null);
  const [loopActive, setLoopActive] = useState(false);

  // Broadcast every knob / tempo change to the parent so the DJ mixer
  // engine can tune the live audio graph.
  useEffect(() => {
    onChannelChange?.(side, { gain, eqLow, eqMid, eqHi, tempo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gain, eqLow, eqMid, eqHi, tempo]);

  useEffect(() => {
    const el = audioRef?.current;
    if (!el) return;
    const onTime = () => {
      const t = el.currentTime || 0;
      setCur(t);
      // Auto-loop: if we've passed loopOut, seek back to loopIn
      if (loopActive && loopIn != null && loopOut != null && t >= loopOut) {
        try { el.currentTime = loopIn; } catch {}
      }
    };
    const onMeta = () => setDur(el.duration || 0);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
    };
  }, [audioRef, track?.id, loopActive, loopIn, loopOut]);

  // Clear loop state when track changes
  useEffect(() => {
    setLoopIn(null); setLoopOut(null); setLoopActive(false);
  }, [track?.id]);

  const loopInAtNow = () => {
    const t = audioRef?.current?.currentTime ?? 0;
    setLoopIn(t);
    if (loopOut != null && loopOut > t) setLoopActive(true);
  };
  const loopOutAtNow = () => {
    const t = audioRef?.current?.currentTime ?? 0;
    setLoopOut(t);
    if (loopIn != null && t > loopIn) setLoopActive(true);
  };
  const autoLoopBeats = (beats) => {
    // Use detected BPM if available, otherwise fall back to 120.
    const effectiveBpm = (bpm && bpm > 0 ? bpm : 120);
    const secs = (60 / effectiveBpm) * beats * (1 + (tempo - 0.5) * 0.16);
    const t0 = audioRef?.current?.currentTime ?? 0;
    setLoopIn(t0);
    setLoopOut(t0 + secs);
    setLoopActive(true);
  };
  const clearLoop = () => { setLoopIn(null); setLoopOut(null); setLoopActive(false); };

  // Apply tempo (±8%) to audio element playbackRate.
  useEffect(() => {
    if (audioRef?.current) {
      const rate = 1 + (tempo - 0.5) * 0.16;
      try { audioRef.current.playbackRate = rate; } catch {}
    }
  }, [tempo, audioRef, track?.id]);

  // Hot cue persistence
  useEffect(() => {
    if (!track?.id) { setHotCues(Array(8).fill(null)); return; }
    try {
      const raw = localStorage.getItem(`tapin_hotcues:${storageKey}:${side}:${track.id}`);
      setHotCues(raw ? JSON.parse(raw) : Array(8).fill(null));
    } catch { setHotCues(Array(8).fill(null)); }
  }, [track?.id, side, storageKey]);

  const saveCues = (next) => {
    setHotCues(next);
    if (track?.id) {
      try { localStorage.setItem(`tapin_hotcues:${storageKey}:${side}:${track.id}`, JSON.stringify(next)); } catch {}
    }
  };

  const onCueClick = (i, e) => {
    if (!audioRef?.current || !track) return;
    if (e.shiftKey) {
      const next = [...hotCues]; next[i] = null; saveCues(next); return;
    }
    if (hotCues[i] == null) {
      const next = [...hotCues]; next[i] = audioRef.current.currentTime || 0; saveCues(next);
    } else {
      audioRef.current.currentTime = hotCues[i];
      if (!playing) onTogglePlay?.();
    }
  };

  const pct = dur > 0 ? Math.min(100, (cur / dur) * 100) : 0;
  const fmt = (s) => {
    if (!isFinite(s) || s < 0) s = 0;
    const m = Math.floor(s / 60);
    const ss = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${ss}`;
  };
  const remaining = Math.max(0, dur - cur);
  const tempoPct = ((tempo - 0.5) * 16).toFixed(1); // ±8%

  const cueColors = [
    "bg-red-500/90", "bg-orange-500/90", "bg-amber-300/90", "bg-lime-500/90",
    "bg-emerald-500/90", "bg-sky-500/90", "bg-fuchsia-500/90", "bg-violet-500/90",
  ];
  const accentRing = accent === "rose"
    ? "border-rose-400/30 shadow-[0_0_24px_rgba(244,63,94,0.18)]"
    : "border-sky-400/30 shadow-[0_0_24px_rgba(56,189,248,0.18)]";
  const accentTxt = accent === "rose" ? "text-rose-300" : "text-sky-300";
  const accentBg  = accent === "rose" ? "bg-rose-400" : "bg-sky-400";
  const accentSync = accent === "rose"
    ? "bg-rose-500/20 text-rose-200 border-rose-500/40"
    : "bg-sky-500/20 text-sky-200 border-sky-500/40";

  return (
    <div data-testid={`serato-deck-${side.toLowerCase()}`} className={`rounded-xl bg-[#0B0F18] border ${accentRing} overflow-hidden`}>
      {/* Info strip */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-white/5 bg-gradient-to-r from-[#070B13] to-[#0B0F18]">
        <div className={`text-[10px] uppercase tracking-widest font-black ${accentTxt}`}>DECK {side}</div>
        {track?._spotify && (
          <span
            data-testid={`serato-deck-${side.toLowerCase()}-spotify-badge`}
            title="Spotify track — monitor only. EQ / SYNC / Scratch / FX disabled. Listeners on the in-app player hear silence on this deck unless you broadcast via OBS."
            className="inline-flex items-center gap-1 bg-emerald-500/30 text-emerald-100 border border-emerald-400/50 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider"
          >
            <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-current"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.479-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.78-.179-.9-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/></svg>
            SPOTIFY
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-white font-display font-black text-base leading-tight truncate">
            {track?.title || "— no track loaded —"}
          </div>
          <div className="text-white/60 text-[11px] truncate">{track?.artist || ""}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9px] uppercase tracking-widest text-white/40">BPM</div>
          <div className="font-mono text-2xl font-black leading-none text-white" data-testid={`serato-deck-${side.toLowerCase()}-bpm`}>
            {bpmAnalyzing ? "…" : (bpm ? bpm.toFixed(1) : "---")}
          </div>
        </div>
      </div>

      {/* Progress micro-bar */}
      <div className="relative h-1 bg-white/5">
        <div className={`absolute inset-y-0 left-0 ${accentBg}`} style={{ width: `${pct}%` }} />
      </div>

      <div className="p-3 space-y-3">
        {/* Hot cue pads */}
        <div className="grid grid-cols-8 gap-1.5" data-testid={`serato-deck-${side.toLowerCase()}-cues`}>
          {hotCues.map((t, i) => (
            <button
              key={i}
              data-testid={`serato-deck-${side.toLowerCase()}-cue-${i + 1}`}
              onClick={(e) => onCueClick(i, e)}
              title={t == null ? "Empty — click to set cue · Shift+Click to clear" : `Jump to ${fmt(t)} · Shift+Click to clear`}
              className={`h-9 rounded font-black text-[10px] text-black transition active:scale-95 border border-black/40 ${
                t == null ? "bg-white/10 text-white/40 hover:bg-white/20" : cueColors[i]
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {/* Turntable + channel strip */}
        <div className="flex items-start gap-3">
          <Turntable
            side={side}
            accent={accent}
            spinning={playing}
            tonearmDown={!!track}
            cover={track?.cover_url}
            bpm={bpm}
            audioRef={audioRef}
            getRestoreRate={() => 1 + (tempoRef.current - 0.5) * 0.16}
            onScratchStart={() => {
              // Force-open this deck's channel via the parent so the rub is
              // audible regardless of crossfader position. Without this, a
              // user touching the platter while the deck isn't on-air gets
              // total silence.
              onScratchingChange?.(side, true);
            }}
            onScratchEnd={() => {
              onScratchingChange?.(side, false);
            }}
          />

          {/* Console channel strip */}
          <div className="flex-1 min-w-0 space-y-2">
            <div className="grid grid-cols-4 gap-1.5">
              <Knob label="GAIN" value={gain} onChange={setGain} accent={accent} testid={`serato-deck-${side.toLowerCase()}-gain`} />
              <Knob label="HI" value={eqHi} onChange={setEqHi} accent={accent} testid={`serato-deck-${side.toLowerCase()}-hi`} />
              <Knob label="MID" value={eqMid} onChange={setEqMid} accent={accent} testid={`serato-deck-${side.toLowerCase()}-mid`} />
              <Knob label="LOW" value={eqLow} onChange={setEqLow} accent={accent} testid={`serato-deck-${side.toLowerCase()}-low`} />
            </div>

            {/* Tempo slider */}
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded px-2 py-1.5">
              <div className="text-[9px] uppercase tracking-widest text-white/50 font-black">TEMPO</div>
              <input
                data-testid={`serato-deck-${side.toLowerCase()}-tempo`}
                type="range" min="0" max="1" step="0.005"
                value={tempo}
                onChange={(e) => setTempo(parseFloat(e.target.value))}
                className="flex-1 accent-tv-accent h-1"
              />
              <div className={`font-mono text-[10px] min-w-[42px] text-right ${tempo > 0.5 ? "text-rose-300" : tempo < 0.5 ? "text-sky-300" : "text-white/60"}`}>
                {tempo === 0.5 ? "0.0%" : (tempoPct > 0 ? `+${tempoPct}%` : `${tempoPct}%`)}
              </div>
            </div>

            {/* Transport */}
            <div className="grid grid-cols-4 gap-1.5">
              <button
                data-testid={`serato-deck-${side.toLowerCase()}-cue`}
                onClick={onCue}
                className="bg-white/5 hover:bg-white/15 border border-white/10 text-white font-black text-[11px] py-2 rounded"
              >CUE</button>
              <button
                data-testid={`serato-deck-${side.toLowerCase()}-sync`}
                onClick={() => {
                  // TRUE beat-sync: if both BPMs are known, set this deck's
                  // tempo slider so its effective BPM matches the peer's.
                  // desiredRate = peerBpm / myBpm; tempo 0.5 = 1.0x, ±0.5 = ±8%
                  // so slider_value = (desiredRate - 1) / 0.16 + 0.5 clamped 0..1
                  if (bpm && peerBpm && bpm > 0 && peerBpm > 0) {
                    const peerEffective = peerBpm * (1 + (peerTempo - 0.5) * 0.16);
                    const desiredRate = peerEffective / bpm;
                    const slider = Math.max(0, Math.min(1, (desiredRate - 1) / 0.16 + 0.5));
                    setTempo(slider);
                    toast.success(`Beat-synced to ${peerBpm.toFixed(1)} BPM`, { id: `sync-${side}` });
                  } else {
                    // Fallback: copy peer's tempo slider directly. Without
                    // BPM detection this won't beat-match the drums but it
                    // at least pitches the two decks together so harmony /
                    // pad layers don't clash. Surface the limitation as a
                    // toast so the host knows why it doesn't feel beat-locked.
                    setTempo(peerTempo);
                    if (!peerLoaded && !track) {
                      // Genuinely no tracks loaded anywhere.
                      toast.info("No peer track to sync to", {
                        id: `sync-${side}`,
                        description: "Load a track on the other deck first, then press SYNC.",
                      });
                    } else if (bpmAnalyzing) {
                      toast.info("Analyzing BPM…", {
                        id: `sync-${side}`,
                        description: "Try again in a second once analysis finishes.",
                      });
                    } else {
                      toast.info("BPM not detected — pitch slider copied", {
                        id: `sync-${side}`,
                        description: "Beat-sync needs a direct-playable URL (MP3 / archive.org). Spotify and YouTube can't be analyzed.",
                      });
                    }
                  }
                }}
                className={`${accentSync} relative border font-black text-[11px] py-2 rounded ${Math.abs(tempo - peerTempo) < 0.005 ? "ring-2 ring-tv-accent/60" : ""}`}
                title={bpm && peerBpm
                  ? `BEAT SYNC · match ${bpm.toFixed(1)} → ${peerBpm.toFixed(1)} BPM`
                  : `BPM not detected${bpmAnalyzing ? " (analyzing…)" : ""} — falling back to slider copy. Streaming sources (YouTube/Spotify) can't be analyzed.`}
              >
                SYNC
                {/* Beat-match readiness indicator: green dot when both BPMs
                    are known (true beat sync), amber when one or both are
                    missing (slider-copy fallback). Keeps the host informed
                    why a SYNC press might not feel like a beat-match. */}
                <span
                  data-testid={`serato-deck-${side.toLowerCase()}-sync-readiness`}
                  className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full ${
                    bpm && peerBpm
                      ? "bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.7)]"
                      : bpmAnalyzing
                      ? "bg-amber-300 animate-pulse"
                      : "bg-amber-400/70"
                  }`}
                />
              </button>
              <button
                data-testid={`serato-deck-${side.toLowerCase()}-headphone`}
                onClick={onToggleCue}
                title="Headphone cue — play this deck at full volume regardless of crossfader position, so you can audition before mixing in."
                aria-pressed={cueEnabled}
                className={`border font-black text-[11px] py-2 rounded ${cueEnabled ? "bg-amber-400 text-black border-amber-400" : "bg-white/5 hover:bg-white/15 text-white border-white/10"}`}
              >🎧</button>
              <button
                data-testid={`serato-deck-${side.toLowerCase()}-play`}
                onClick={onTogglePlay}
                className="bg-white text-black font-black text-[11px] py-2 rounded inline-flex items-center justify-center gap-1"
              >
                {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                {playing ? "PAUSE" : "PLAY"}
              </button>
            </div>

            {/* LOOP */}
            <div className="bg-black/40 border border-white/10 rounded px-2 py-1.5 space-y-1">
              <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-white/50 font-black">
                <span>LOOP</span>
                {loopActive && loopIn != null && loopOut != null && (
                  <span className="text-tv-accent font-mono">{fmt(loopIn)} – {fmt(loopOut)} · {((loopOut - loopIn)).toFixed(2)}s</span>
                )}
              </div>
              <div className="grid grid-cols-5 gap-1">
                <button
                  data-testid={`serato-deck-${side.toLowerCase()}-loop-in`}
                  onClick={loopInAtNow}
                  className={`font-black text-[9px] py-1 rounded border ${loopIn != null ? "bg-emerald-500/25 border-emerald-500/50 text-emerald-100" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/15"}`}
                >IN</button>
                <button
                  data-testid={`serato-deck-${side.toLowerCase()}-loop-out`}
                  onClick={loopOutAtNow}
                  className={`font-black text-[9px] py-1 rounded border ${loopOut != null ? "bg-emerald-500/25 border-emerald-500/50 text-emerald-100" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/15"}`}
                >OUT</button>
                <button
                  data-testid={`serato-deck-${side.toLowerCase()}-loop-4`}
                  onClick={() => autoLoopBeats(4)}
                  className="bg-white/5 hover:bg-white/15 border border-white/10 text-white/80 font-black text-[9px] py-1 rounded"
                >4</button>
                <button
                  data-testid={`serato-deck-${side.toLowerCase()}-loop-8`}
                  onClick={() => autoLoopBeats(8)}
                  className="bg-white/5 hover:bg-white/15 border border-white/10 text-white/80 font-black text-[9px] py-1 rounded"
                >8</button>
                <button
                  data-testid={`serato-deck-${side.toLowerCase()}-loop-16`}
                  onClick={() => autoLoopBeats(16)}
                  className="bg-white/5 hover:bg-white/15 border border-white/10 text-white/80 font-black text-[9px] py-1 rounded"
                >16</button>
              </div>
              <div className="flex gap-1">
                <button
                  data-testid={`serato-deck-${side.toLowerCase()}-loop-toggle`}
                  onClick={() => setLoopActive((v) => !v)}
                  disabled={loopIn == null || loopOut == null || loopOut <= loopIn}
                  className={`flex-1 font-black text-[9px] py-1 rounded disabled:opacity-30 ${loopActive ? "bg-tv-accent text-black" : "bg-white/5 text-white/70 hover:bg-white/15 border border-white/10"}`}
                >{loopActive ? "LOOP ON" : "LOOP OFF"}</button>
                <button
                  data-testid={`serato-deck-${side.toLowerCase()}-loop-clear`}
                  onClick={clearLoop}
                  className="bg-white/5 hover:bg-red-500/30 text-white/60 hover:text-red-200 border border-white/10 text-[9px] font-black px-2 rounded"
                  title="Clear loop"
                >×</button>
              </div>
            </div>

            {/* Time strip */}
            <div className="flex items-center justify-between font-mono text-[11px] text-white/70">
              <span data-testid={`serato-deck-${side.toLowerCase()}-time`}>{fmt(cur)}</span>
              <span className="opacity-50">—{fmt(remaining)}</span>
            </div>

            <div className="flex gap-1.5">
              {track && (
                <button
                  data-testid={`serato-deck-${side.toLowerCase()}-eject`}
                  onClick={onClear}
                  className="flex-1 text-[10px] text-white/50 hover:text-white border border-white/10 rounded py-1"
                >
                  Eject
                </button>
              )}
              <button
                data-testid={`serato-deck-${side.toLowerCase()}-back5`}
                onClick={() => audioRef?.current && (audioRef.current.currentTime = Math.max(0, (audioRef.current.currentTime || 0) - 5))}
                className="flex-1 text-[10px] text-white/60 hover:text-white border border-white/10 rounded py-1 inline-flex items-center justify-center gap-1"
                title="Back 5s"
              >
                <SkipBack className="w-3 h-3" /> -5s
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Turntable — realistic platter with Tap IN Radio slipmat + tonearm  */
/* ------------------------------------------------------------------ */
function Turntable({ side = "A", accent, spinning, tonearmDown, cover, bpm, audioRef, onScratchStart, onScratchEnd, getRestoreRate }) {
  const plateRef = useRef(null);
  const [scratching, setScratching] = useState(false);
  const [manualAngle, setManualAngle] = useState(0);
  const SIZE = 240;  // was 190 — user asked for bigger records

  // Convert an angular delta (degrees) into an audio-time delta using a
  // 33⅓ RPM record: one full rotation = 60/33.33 ≈ 1.8 seconds of audio.
  const DEG_TO_SEC = 1.8 / 360;

  const onPointerDown = (e) => {
    if (!audioRef?.current) return;
    e.preventDefault();
    const el = plateRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let lastAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
    const audio = audioRef.current;
    const wasPaused = !!audio?.paused;
    const wasMuted = !!audio?.muted;

    setScratching(true);
    onScratchStart?.();
    // Note: the engine's window-level pointerdown handler resumes the
    // AudioContext if it was suspended (see useDjMixerEngine.js), so we
    // don't need to do that here.

    // Scratching drives the audio playbackRate from how fast the finger
    // rotates the platter. We accumulate angular delta from every
    // pointermove and sample it ONCE per animation frame so the rate is
    // smooth and clickless.
    //
    //   • Forward motion → drives playbackRate (smooth play forward).
    //   • Reverse motion → seeks currentTime backward per-frame at the
    //     equivalent vinyl distance, while rate stays at the floor (so
    //     reverse is audible without glitching).
    //   • No motion at all → audio.muted (vinyl-friction silence).
    //
    // Mapping: 33⅓ RPM ≈ 200°/s at 1× rate. We clamp to [0.0625, 2.5].
    const DEG_PER_SEC_AT_1X = 200;
    const RATE_FLOOR = 0.0625;     // Chromium-clamp floor — also our reverse rate
    const RATE_CEIL  = 2.5;
    // Hysteresis on the friction-mute so micro-jitter doesn't strobe the
    // mute on/off (clicks). MUTE if angVel < ENTER for 2 consecutive
    // frames; UNMUTE the moment angVel >= EXIT.
    const FRICTION_ENTER_DEG_S = 1.5;   // tighter: only true "held still"
    const FRICTION_EXIT_DEG_S  = 6.0;
    let stillFrames = 0;
    let frictionMuted = false;

    let pendingDelta = 0;     // accumulated deg since last frame
    let rafId = null;
    let lastFrameTs = performance.now();

    // Make sure we're playing — scratch on a paused track has to start
    // playback so the rub is audible. We restore on release.
    try { audio.playbackRate = RATE_FLOOR; } catch {}
    if (wasPaused) { audio.play().catch(() => {}); }

    const frame = () => {
      rafId = requestAnimationFrame(frame);
      const now = performance.now();
      const dt = Math.max(0.001, (now - lastFrameTs) / 1000);
      lastFrameTs = now;
      const angVel = pendingDelta / dt; // deg/sec
      const absVel = Math.abs(angVel);
      pendingDelta = 0;
      const rateMag = Math.min(RATE_CEIL, absVel / DEG_PER_SEC_AT_1X);

      // Friction-mute hysteresis
      if (absVel < FRICTION_ENTER_DEG_S) {
        stillFrames += 1;
        if (stillFrames >= 2 && !frictionMuted) {
          try { audio.muted = true; } catch {}
          frictionMuted = true;
        }
      } else {
        stillFrames = 0;
        if (frictionMuted && absVel > FRICTION_EXIT_DEG_S) {
          try { audio.muted = wasMuted; } catch {}
          frictionMuted = false;
        }
      }

      if (frictionMuted) {
        try { audio.playbackRate = RATE_FLOOR; } catch {}
        return;
      }

      if (angVel >= 0) {
        // Forward: drive playbackRate, no seeking ⇒ smooth.
        try { audio.playbackRate = Math.max(RATE_FLOOR, rateMag); } catch {}
      } else {
        // Reverse: <audio> can't play backwards, so seek backwards per
        // frame while keeping rate at the floor. This produces the
        // characteristic "rrrrh" rub on a real Serato deck.
        try {
          audio.playbackRate = RATE_FLOOR;
          const seconds = rateMag * dt; // how far vinyl would travel backward
          const t = Math.max(0, (audio.currentTime || 0) - seconds);
          audio.currentTime = t;
        } catch {}
      }
    };
    rafId = requestAnimationFrame(frame);

    const move = (ev) => {
      const a = Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180 / Math.PI;
      let delta = a - lastAngle;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      lastAngle = a;
      pendingDelta += delta;
      setManualAngle((v) => v + delta);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      if (rafId) cancelAnimationFrame(rafId);
      setScratching(false);
      setManualAngle(0);
      // Always restore the original mute state on release.
      try { audio.muted = wasMuted; } catch {}
      // Restore the rate from the CURRENT tempo (not the captured original)
      // so a SYNC press / tempo nudge during the scratch is honored when the
      // user lets go.
      try {
        const restored = getRestoreRate ? getRestoreRate() : 1;
        audio.playbackRate = restored;
      } catch {}
      if (wasPaused) { try { audio.pause(); } catch {} }
      onScratchEnd?.();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };


  return (
    <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
      {/* Vinyl disc base — black with subtle radial grooves */}
      <div
        ref={plateRef}
        data-testid={`serato-deck-${side.toLowerCase()}-plate`}
        onPointerDown={onPointerDown}
        className={`absolute inset-0 rounded-full select-none touch-none ${spinning && !scratching ? "turntable-spin" : ""} ${scratching ? "cursor-grabbing" : "cursor-grab"}`}
        style={{
          background: `
            radial-gradient(circle at 50% 50%, #0a0a0a 0 14%, transparent 14%),
            repeating-radial-gradient(circle at 50% 50%, #141414 0px, #141414 1.5px, #1b1b1b 2px, #1b1b1b 3.5px),
            radial-gradient(circle at 50% 50%, #0a0a0a 0%, #000 80%)`,
          boxShadow: "inset 0 0 40px rgba(0,0,0,0.85), 0 4px 24px rgba(0,0,0,0.8)",
          transform: scratching ? `rotate(${manualAngle}deg)` : undefined,
          transition: scratching ? "none" : undefined,
        }}
        title="Drag to scratch · Click PLAY to resume"
      >
        {/* Light sheen */}
        <div className="absolute inset-0 rounded-full pointer-events-none"
          style={{ background: "conic-gradient(from 45deg, transparent 0deg, rgba(255,255,255,0.06) 40deg, transparent 80deg, transparent 220deg, rgba(255,255,255,0.04) 260deg, transparent 300deg)" }}
        />
        {/* Tap IN Radio slipmat */}
        <div
          className="absolute rounded-full overflow-hidden pointer-events-none"
          style={{
            inset: "20%",
            backgroundImage: `url(${TAPIN_RADIO_LOGO})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "inset 0 0 18px rgba(0,0,0,0.85)",
          }}
        >
          {cover && (
            <div
              className="absolute inset-0 mix-blend-multiply opacity-40"
              style={{ backgroundImage: `url(${cover})`, backgroundSize: "cover", backgroundPosition: "center" }}
            />
          )}
        </div>
        {/* Center spindle */}
        <div className="absolute left-1/2 top-1/2 w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-zinc-300 shadow-[0_0_6px_rgba(255,255,255,0.4)] pointer-events-none" />
      </div>

      {/* Stroboscopic dots around rim */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 60 }).map((_, i) => (
          <span
            key={i}
            className="absolute left-1/2 top-[4px] w-[2px] h-[3px] bg-white/25"
            style={{ transformOrigin: `50% ${(SIZE / 2) - 3}px`, transform: `translateX(-50%) rotate(${i * 6}deg)` }}
          />
        ))}
      </div>

      {/* Pitch ring border */}
      <div className="absolute inset-0 rounded-full border border-white/10 pointer-events-none" />

      {/* Tonearm (right-side arm pivoting from top-right) */}
      <div
        className="absolute pointer-events-none"
        style={{
          right: -10, top: 8,
          width: 22, height: 120,
          transformOrigin: "top right",
          transform: `rotate(${tonearmDown ? -28 : -48}deg)`,
          transition: "transform 600ms cubic-bezier(0.5,0,0.2,1)",
        }}
      >
        <div className="absolute right-1 top-0 w-3 h-3 rounded-full bg-zinc-300 shadow" />
        <div className="absolute right-[7px] top-3 bottom-6 w-[2px] bg-gradient-to-b from-zinc-300 to-zinc-500" />
        <div className="absolute right-0 bottom-3 w-4 h-3 rounded-sm bg-zinc-400 shadow-inner" />
        <div className="absolute right-1 bottom-0 w-2 h-3 bg-zinc-600" />
      </div>

      {/* BPM chip + SCRATCH indicator */}
      <div className={`absolute -left-1 bottom-1 px-1.5 py-0.5 rounded bg-black/80 border text-[9px] font-mono font-black ${accent === "rose" ? "text-rose-300 border-rose-500/40" : "text-sky-300 border-sky-500/40"}`}>
        {bpm ? `${bpm.toFixed(0)} BPM` : "—"}
      </div>
      {scratching && (
        <div className="absolute left-1/2 top-1 -translate-x-1/2 px-1.5 py-0.5 rounded bg-red-500 text-white text-[9px] font-black tracking-widest uppercase animate-pulse pointer-events-none">
          SCRATCH
        </div>
      )}

      <style>{`
        .turntable-spin { animation: turntable-spin 1.8s linear infinite; }
        @keyframes turntable-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Knob — click + vertical-drag to change; visual rotary indicator    */
/* ------------------------------------------------------------------ */
function Knob({ label, value, onChange, accent = "sky", testid }) {
  const ref = useRef(null);
  const angle = -135 + value * 270; // -135° to +135°
  const onPointerDown = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startV = value;
    const move = (ev) => {
      const dy = startY - ev.clientY;
      const v = Math.max(0, Math.min(1, startV + dy / 120));
      onChange(v);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const onDouble = () => onChange(0.5);
  const accentStroke = accent === "rose" ? "stroke-rose-400" : "stroke-sky-400";
  const accentTxt = accent === "rose" ? "text-rose-300" : "text-sky-300";
  return (
    <button
      type="button"
      data-testid={testid}
      ref={ref}
      onPointerDown={onPointerDown}
      onDoubleClick={onDouble}
      title={`${label} — drag up/down to change. Double-click to reset.`}
      className="bg-black/40 border border-white/10 rounded flex flex-col items-center py-1.5 px-1 hover:bg-black/60 focus:outline-none focus:ring-1 focus:ring-tv-accent/70 select-none"
    >
      <div className="text-[8px] uppercase tracking-widest text-white/50 font-black mb-0.5">{label}</div>
      <svg width="34" height="34" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="16" fill="#0c0f15" stroke="#2a2f3a" strokeWidth="1.5" />
        {/* Arc indicator */}
        <path d="M 7 28 A 16 16 0 1 1 33 28" fill="none" stroke="#1f2732" strokeWidth="2" strokeLinecap="round" />
        <g transform={`rotate(${angle} 20 20)`}>
          <line x1="20" y1="20" x2="20" y2="7" strokeWidth="2.5" strokeLinecap="round" className={accentStroke} />
        </g>
        <circle cx="20" cy="20" r="3" fill="#20242d" stroke="#4a505c" />
      </svg>
      <div className={`text-[9px] font-mono font-black mt-0.5 ${accentTxt}`}>
        {value === 0.5 ? "0" : ((value - 0.5) * 2 * 100).toFixed(0)}
      </div>
    </button>
  );
}
